import { Injectable } from '@angular/core';
import { concatLatestFrom } from '@ngrx/operators';
import { Observable } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';

import { ResourceEditorEffects } from '../../../effects/resource-editor.effects';
import { SaveProtocolCascadeEffects } from '../../../effects/save-protocol-cascade.effects';
import { AssignedResourcesState } from '../../../state/assigned-resources.state';
import { ResourceEditorState } from '../../../state/resource-editor.state';
import { ResourceDraftData, StagedResourcesState } from '../../../state/staged-resources.state';

/**
 * Persist-now consumer for the standalone route host (`ResourceEditorPageComponent`).
 *
 * `ResourceEditorComponent` is a pure draft producer — its `save`/`cancel` emit
 * a one-shot `ResourceEditorEffects.outcome$` event and never persist. This facade
 * owns the route-side decision: on `saved` it STAGES the root resource's draft
 * into the shared ledger and then triggers the resource cascade.
 *
 * Why stage the root here? The cascade (`consolidateResource`) reads ONLY the
 * staging ledger + the containment tree — it has no dependency on
 * `ResourceEditorState`. The root resource is just the deepest ledger row (it
 * persists last in the bottom-up walk), so the page facade must write it into
 * the ledger before triggering, exactly as the manager keeps the nested rows in
 * sync. Keeping this orchestration in a facade — not the component — follows the
 * UI-layer rule that components hold no business logic.
 *
 * The (future) dialog consumer reuses the same producer with a *different*
 * decision (stage the draft for the deferred nested save) — that lives in its
 * own consumer, not here.
 */
@Injectable()
export class ResourceEditorPageFacade {
  constructor(
    private readonly editorEffects: ResourceEditorEffects,
    private readonly state: ResourceEditorState,
    private readonly stagedState: StagedResourcesState,
    private readonly assignedState: AssignedResourcesState,
    private readonly cascadeEffects: SaveProtocolCascadeEffects
  ) {}

  /**
   * Seeds the editor (create mode when `resourceId` is null) and wires the
   * persist-now reaction: every `saved` outcome stages the root draft into the
   * ledger and triggers the cascade. Built on `state.effect` so the ongoing
   * subscription's teardown follows the per-page state lifecycle, and a retry
   * after a save error re-stages and re-triggers naturally.
   */
  public readonly initialize = this.state.effect((resourceId$: Observable<string | null>) =>
    resourceId$.pipe(
      tap((resourceId) => this.editorEffects.loadResource(resourceId)),
      switchMap(() => this.editorEffects.outcome$),
      filter((outcome) => outcome.kind === 'saved'),
      concatLatestFrom(() => [this.state.draft$, this.state.id$, this.assignedState.containers$]),
      tap(([, draft, id, containers]) => {
        // The root is just the deepest ledger row; mirror the tree's nested
        // count onto it so the persisted resource carries the right child set.
        const draftData: ResourceDraftData = {
          ...draft,
          nestedResourcesCount: containers[draft.id]?.childIds.length ?? 0,
        };
        if (id === null) {
          // Create mode: `draft.id` is the placeholder containerId — a pending row.
          this.stagedState.addPendingResource(draftData);
        } else {
          // Edit mode: an override keyed by the persisted resource id.
          this.stagedState.setResourceOverride({ original: draftData, override: draftData });
        }
        this.cascadeEffects.consolidateResource();
      })
    )
  );

  /** Navigation intent: the cascade finished a full resource-rooted save. */
  public readonly saved$: Observable<void> = this.cascadeEffects.cascadeCompleted$;

  /** Navigation intent: the user cancelled the edit. */
  public readonly cancelled$: Observable<void> = this.editorEffects.outcome$.pipe(
    filter((outcome) => outcome.kind === 'cancelled'),
    map(() => undefined)
  );
}
